import {
    DELIMITER_CHARS,
    EAP_SETTINGS_IMPORT_COLUMN_HEADERS,
    EapSettingsImportFlowData,
} from '../settings-devices-eap-settings-import-modal/settings-devices-eap-settings-import-modal.model';
import {
    EapImportFieldKey,
    EapImportMappings,
    MissingCertificatesByType,
    UploadedImportCertificateFile,
} from './settings-devices-eap-settings-importing.model';
import { EapImportNamingStrategy } from '../service/eap-settings-rest.api';

export const normalizeCipherType = (value: string): string => {
    const normalized = value.trim().toUpperCase();

    if (normalized === 'AES' || normalized === 'CCMP') {
        return 'AES';
    }

    if (normalized === 'TKIP') {
        return 'TKIP';
    }

    if (['AES TKIP', 'TKIP AES', 'CCMP TKIP', 'TKIP CCMP', 'AES-TKIP', 'CCMP-TKIP'].includes(normalized)) {
        return 'BOTH';
    }

    return normalized;
};

export const getRowCellValue = (row: string[], column: string): string =>
    row[Number.parseInt(column, 10) - 1]?.trim() ?? '';

export const getMappedValueForRow = (
    fieldKey: EapImportFieldKey,
    row: string[],
    mappings: EapImportMappings
): string => {
    const mapping = mappings[fieldKey];

    if (mapping.source === 'FILE') {
        return mapping.fileColumn ? getRowCellValue(row, mapping.fileColumn) : '';
    }

    if (
        fieldKey === 'caCertificateFilename' ||
        fieldKey === 'clientCertificateFilename' ||
        fieldKey === 'clientPrivateKeyFilename'
    ) {
        return mapping.fixedFileName;
    }

    return mapping.fixedValue;
};

export const getDataRowsFromImportFlow = (importFlowData?: EapSettingsImportFlowData): string[][] => {
    if (!importFlowData?.fileContent) {
        return [];
    }

    const delimiter = DELIMITER_CHARS[importFlowData.delimiter];
    const lines = importFlowData.fileContent
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

    const startRow = importFlowData.columnHeaders === EAP_SETTINGS_IMPORT_COLUMN_HEADERS.WITH_HEADERS ? 1 : 0;

    return lines.slice(startRow).map((line) => line.split(delimiter).map((cell) => cell.trim()));
};

export const getCertFilenamesForFieldFromContent = (params: {
    importFlowData?: EapSettingsImportFlowData;
    mappings: EapImportMappings;
    fieldKey: 'caCertificateFilename' | 'clientCertificateFilename' | 'clientPrivateKeyFilename';
}): string[] => {
    const { importFlowData, mappings, fieldKey } = params;
    const mapping = mappings[fieldKey];

    if (mapping.source !== 'FILE' || !mapping.fileColumn || !importFlowData?.fileContent) {
        return [];
    }

    const colIndex = Number.parseInt(mapping.fileColumn, 10) - 1;
    const rows = getDataRowsFromImportFlow(importFlowData);
    const filenames = rows.map((row) => row[colIndex]?.trim()).filter((name): name is string => !!name);

    return [...new Set(filenames)];
};

export const buildImportPayloadFromState = (params: {
    importFlowData?: EapSettingsImportFlowData;
    mappings: EapImportMappings;
    selectedDevice: string;
    files: UploadedImportCertificateFile[];
    missingCertificatesByType: MissingCertificatesByType;
}) => {
    const { importFlowData, mappings, selectedDevice, files, missingCertificatesByType } = params;
    const rows = getDataRowsFromImportFlow(importFlowData);
    const namingStrategy: EapImportNamingStrategy = mappings.name.source === 'FILE' ? 'FILE' : 'PREFIX';
    const namePrefix = mappings.name.fixedValue;

    return {
        namingStrategy,
        eapContent: rows.map((row, index) => ({
            name:
                namingStrategy === 'PREFIX' ? `${namePrefix}${index + 1}` : getMappedValueForRow('name', row, mappings),
            serialNumber: getRowCellValue(row, selectedDevice),
            ssid: getMappedValueForRow('ssid', row, mappings),
            cipherType: normalizeCipherType(getMappedValueForRow('cipherType', row, mappings)),
            eapType: getMappedValueForRow('eapType', row, mappings).toUpperCase(),
            anonymousUser: getMappedValueForRow('anonymousUser', row, mappings),
            username: getMappedValueForRow('username', row, mappings),
            caCertificateFilename: getMappedValueForRow('caCertificateFilename', row, mappings),
            clientCertificateFilename: getMappedValueForRow('clientCertificateFilename', row, mappings),
            privateKeyFilename: getMappedValueForRow('clientPrivateKeyFilename', row, mappings),
            password: getMappedValueForRow('password', row, mappings),
            privateKeyPassword: getMappedValueForRow('clientPrivateKeyPassword', row, mappings),
        })),
        caCertificates: [
            ...(mappings.caCertificateFilename.fixedFile ? [mappings.caCertificateFilename.fixedFile] : []),
            ...files
                .filter((file) => missingCertificatesByType.caCertificateFilenames.includes(file.name))
                .map((file) => file.file),
        ],
        clientCertificates: [
            ...(mappings.clientCertificateFilename.fixedFile ? [mappings.clientCertificateFilename.fixedFile] : []),
            ...files
                .filter((file) => missingCertificatesByType.clientCertificateFilenames.includes(file.name))
                .map((file) => file.file),
        ],
        privateKeys: [
            ...(mappings.clientPrivateKeyFilename.fixedFile ? [mappings.clientPrivateKeyFilename.fixedFile] : []),
            ...files
                .filter((file) => missingCertificatesByType.privateKeyFilenames.includes(file.name))
                .map((file) => file.file),
        ],
    };
};
